import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { components, CSSObjectWithLabel, OptionProps, SingleValueProps } from 'react-select';
import { CURRENCIES } from '../constants/currencies';
import { Currency } from '../entities/Currency';
import { Badge } from './primitives/Badge';
import { HStack } from './primitives/stack/HStack';
import { SearchSelect } from './Select';

interface Props {
  value: Currency;
  onChange(currency: Currency): void;
  priorityCurrencies?: Currency[];
  codeOnly?: boolean;
  minWidth?: string | number;
}

interface CurrencyOption {
  value: Currency;
  label: string;
}

interface CurrencyOptionProps {
  currency: Currency;
  px?: number;
  codeOnly?: boolean;
}

function CurrencyOption({ currency, px, codeOnly }: CurrencyOptionProps) {
  const { t } = useTranslation('currencies');
  const currencyCode = currency.toUpperCase();
  const currencyName = t(currencyCode);

  return (
    <HStack spacing={2} px={px}>
      <Badge minW="35px" variant="black" size="sm" textAlign="center">
        {currencyCode}
      </Badge>
      <span>{codeOnly ? currencyCode : currencyName}</span>
    </HStack>
  );
}

const DEFAULT_PRIORITY_CURRENCIES: Currency[] = ['EUR', 'GBP', 'USD'];

export function CurrencySelect({ value, onChange, priorityCurrencies, codeOnly, minWidth = '240px' }: Props) {
  const { t } = useTranslation('currencies');

  const selectComponents = useMemo(() => ({
    Option: (props: OptionProps<CurrencyOption>) => (
      <components.Option {...props}>
        <CurrencyOption currency={props.data.value} px={1} codeOnly={codeOnly} />
      </components.Option>
    ),
    SingleValue: (props: SingleValueProps<CurrencyOption>) => (
      <components.SingleValue {...props}>
        <CurrencyOption currency={props.data.value} codeOnly={codeOnly} />
      </components.SingleValue>
    )
  }), [codeOnly]);

  const options = useMemo(() => {
    const mergedPriorities = priorityCurrencies
      ? [...new Set([...DEFAULT_PRIORITY_CURRENCIES, ...priorityCurrencies])]
      : DEFAULT_PRIORITY_CURRENCIES;

    const list = [];

    for (const currencyLowerCase of CURRENCIES) {
      const currency = currencyLowerCase.toUpperCase() as Currency;

      if (currency !== value) {
        const option = { value: currency, label: t(currency) };

        mergedPriorities.includes(currency) ?
          list.unshift(option) :
          list.push(option);
      }
    }

    list.unshift({ value, label: t(value) });

    return list;
  }, [t, value, priorityCurrencies]);

  const selectedOption = options.find(option => option.value === value);

  function onSelect(option: CurrencyOption | null) {
    if (option) {
      onChange(option.value);
    }
  }

  return (
    <SearchSelect<Currency>
      options={options}
      value={selectedOption}
      onChange={onSelect}
      components={selectComponents}
      styles={{
        container: (provided: CSSObjectWithLabel) => ({
          ...provided,
          minWidth
        })
      }}
    />
  );
}